/**
 * GALLOP WATCH HOUSE - Luxury Interactions
 * gallop-luxury.js
 * Handles: sticky header scroll state, lazy load animations,
 * product image hover, authentication badge tooltips,
 * scroll-reveal, and mobile UX enhancements.
 */

(function() {
  'use strict';

  /* =====================================================
   * 1. STICKY HEADER SCROLL STATE
   * Adds .scrolled class to header after 80px scroll.
   * Used by CSS to activate blur + darker bg.
   * ===================================================== */
  const headerWrapper = document.querySelector('.header-wrapper');
  if (headerWrapper) {
    const onScroll = () => {
      if (window.scrollY > 80) {
        headerWrapper.classList.add('scrolled');
      } else {
        headerWrapper.classList.remove('scrolled');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* =====================================================
   * 2. SCROLL REVEAL ANIMATIONS
   * Adds .gwh-revealed class to elements as they enter
   * the viewport, triggering CSS fade-up transitions.
   * ===================================================== */
  const revealElements = document.querySelectorAll(
    '.gwh-reveal, .card-product, .product-card, .gwh-brand-item, .gwh-trust-item, .gwh-why-item, .gwh-spec-row'
  );

  if ('IntersectionObserver' in window && revealElements.length) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            // Stagger delay based on element index within its parent
            const siblings = Array.from(entry.target.parentElement.children);
            const idx = siblings.indexOf(entry.target);
            entry.target.style.transitionDelay = (idx * 80) + 'ms';
            entry.target.classList.add('gwh-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    revealElements.forEach(el => {
      el.classList.add('gwh-reveal');
      revealObserver.observe(el);
    });
  } else {
    // Fallback: reveal all immediately
    revealElements.forEach(el => el.classList.add('gwh-revealed'));
  }

  /* =====================================================
   * 3. LUXURY CURSOR ENHANCEMENT (desktop only)
   * Adds a subtle gold ring cursor on product images.
   * ===================================================== */
  if (window.matchMedia('(pointer: fine)').matches) {
    const cursor = document.createElement('div');
    cursor.className = 'gwh-cursor';
    document.body.appendChild(cursor);

    document.addEventListener('mousemove', (e) => {
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    }, { passive: true });

    const interactiveEls = document.querySelectorAll(
      '.card-product, .card__media, .product-gallery, a, button'
    );
    interactiveEls.forEach(el => {
      el.addEventListener('mouseenter', () => cursor.classList.add('gwh-cursor--active'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('gwh-cursor--active'));
    });
  }

  /* =====================================================
   * 4. AUTHENTICATION BADGE TOOLTIP
   * Shows tooltip on hover over .gwh-auth-badge.
   * ===================================================== */
  document.querySelectorAll('.gwh-auth-badge').forEach(badge => {
    badge.setAttribute('tabindex', '0');
    badge.setAttribute('role', 'button');
    badge.setAttribute('aria-describedby', 'gwh-auth-tooltip');
  });

  /* =====================================================
   * 5. PRODUCT PAGE - STICKY ADD TO CART
   * Shows sticky ATC bar after the main ATC is scrolled past.
   * ===================================================== */
  const mainAtc = document.querySelector('.product-form__submit, [name="add"]');
  const stickyBar = document.querySelector('.gwh-sticky-atc');

  if (mainAtc && stickyBar) {
    const atcObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          stickyBar.classList.add('gwh-sticky-atc--visible');
        } else {
          stickyBar.classList.remove('gwh-sticky-atc--visible');
        }
      },
      { threshold: 0 }
    );
    atcObserver.observe(mainAtc);
  }

  /* =====================================================
   * 6. COLLECTION FILTER PANEL ENHANCEMENTS
   * Smooth accordion open/close for filter groups.
   * ===================================================== */
  document.querySelectorAll('.facets__summary, .facet-filters__summary').forEach(summary => {
    summary.addEventListener('click', () => {
      const details = summary.closest('details');
      if (details) {
        details.classList.toggle('gwh-filter-open');
      }
    });
  });

  /* =====================================================
   * 7. LAZY IMAGE LOADING ENHANCEMENT
   * Adds gwh-img-loaded class after image load for fade-in.
   * ===================================================== */
  document.querySelectorAll('img[loading="lazy"]').forEach(img => {
    if (img.complete) {
      img.classList.add('gwh-img-loaded');
    } else {
      img.addEventListener('load', () => img.classList.add('gwh-img-loaded'));
    }
  });

  /* =====================================================
   * 8. MOBILE TOUCH IMPROVEMENTS
   * Prevent 300ms tap delay on interactive elements.
   * ===================================================== */
  if ('ontouchstart' in window) {
    document.body.classList.add('gwh-touch');
  }

  /* =====================================================
   * 9. ANNOUNCEMENT BAR AUTO-ROTATE
   * Rotates multiple announcement messages every 4s.
   * ===================================================== */
  const announcementItems = document.querySelectorAll('.announcement-bar__message');
  if (announcementItems.length > 1) {
    let currentIdx = 0;
    announcementItems.forEach((item, i) => {
      item.style.display = i === 0 ? 'block' : 'none';
    });
    setInterval(() => {
      announcementItems[currentIdx].style.display = 'none';
      currentIdx = (currentIdx + 1) % announcementItems.length;
      announcementItems[currentIdx].style.display = 'block';
    }, 4000);
  }

  /* =====================================================
   * 10. PRODUCT GALLERY THUMBNAIL ACTIVE STATE
   * Adds gwh-active to the currently focused thumbnail.
   * ===================================================== */
  document.querySelectorAll('.product__media-list, .product-gallery').forEach(gallery => {
    gallery.querySelectorAll('[data-thumbnail], .thumbnail, .product__media-item').forEach(thumb => {
      thumb.addEventListener('click', () => {
        gallery.querySelectorAll('[data-thumbnail], .thumbnail, .product__media-item')
          .forEach(t => t.classList.remove('gwh-active'));
        thumb.classList.add('gwh-active');
      });
    });
  });

  /* =====================================================
   * 11. GWH-SPEC TABLE: HIGHLIGHT ROW ON HOVER
   * ===================================================== */
  document.querySelectorAll('.gwh-spec-row').forEach(row => {
    row.addEventListener('mouseenter', () => row.classList.add('gwh-spec-row--hover'));
    row.addEventListener('mouseleave', () => row.classList.remove('gwh-spec-row--hover'));
  });

  console.log('[Gallop Watch House] Luxury JS loaded.');

})();
